import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenLine } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/supabase";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const plan = params.get("plan");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bem-vinda de volta!");
      if (plan) {
        nav(`/app/checkout?plan=${plan}`);
      } else {
        nav("/app/dashboard");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F6F5F0] grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1594665964721-d96c7e10981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxwcm9vZnJlYWRpbmclMjBkZXNrfGVufDB8fHx8MTc3NzU2MTc3Mnww&ixlib=rb-4.1.0&q=85"
          alt="Mesa de revisão"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A1A1A]/20" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#D9381E] mb-3">RevisaPro</div>
          <h2 className="font-serif text-4xl leading-tight">“Precificar deixou de ser chute. Virou estratégia.”</h2>
          <p className="mt-4 text-white/80 text-sm">— Ana Luiza, revisora acadêmica</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
            <span className="font-serif text-2xl font-bold">RevisaPro</span>
          </Link>
          <h1 className="font-serif text-4xl mb-2">Entrar</h1>
          <p className="text-[#4A4A4A] mb-8">Bom te ver de novo.</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest mb-2 block">E-mail</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required className="rounded-none border-black/20 focus-visible:ring-[#D9381E]"
                data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest mb-2 block">Senha</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required className="rounded-none border-black/20 focus-visible:ring-[#D9381E]"
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white py-6"
              data-testid="login-submit-btn">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" title="Esqueci minha senha" className="text-xs font-mono uppercase tracking-widest text-[#8A8A8A] hover:text-[#D9381E] transition-colors">
                Esqueci minha senha
              </Link>
            </div>
          </form>
          <p className="mt-8 text-sm text-[#4A4A4A]">
            Ainda não tem conta?{" "}
            <Link
              to={plan ? `/cadastro?plan=${plan}` : "/cadastro"}
              className="text-[#D9381E] underline underline-offset-2"
            >
              Cadastre-se
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
